<script>
import { mapGetters } from 'vuex';
import AgentTable from './components/overview/AgentTable.vue';
import AgentDistributionCard from './components/overview/AgentDistributionCard.vue';
import MetricCard from './components/overview/MetricCard.vue';
import { OVERVIEW_METRICS } from './constants';
import ReportHeatmap from './components/Heatmap.vue';

import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import ReportHeader from './components/ReportHeader.vue';
export const FETCH_INTERVAL = 60000;

export default {
  name: 'LiveReports',
  components: {
    ReportHeader,
    AgentTable,
    AgentDistributionCard,
    MetricCard,
    ReportHeatmap,
  },
  data() {
    return {
      // always start with 0, this is to manage the pagination in tanstack table
      // when we send the data, we do a +1 to this value
      pageIndex: 0,
    };
  },
  computed: {
    ...mapGetters({
      agentStatus: 'agents/getAgentStatus',
      agents: 'agents/getAgents',
      accountConversationMetric: 'getAccountConversationMetric',
      agentConversationMetric: 'getAgentConversationMetric',
      accountConversationHeatmap: 'getAccountConversationHeatmapData',
      uiFlags: 'getOverviewUIFlags',
      agentSummaryReports: 'summaryReports/getAgentSummaryReports',
      summaryReportsUIFlags: 'summaryReports/getUIFlags',
    }),
    agentStatusMetrics() {
      let metric = {};
      Object.keys(this.agentStatus).forEach(key => {
        const metricName = this.$t(
          `OVERVIEW_REPORTS.AGENT_STATUS.${OVERVIEW_METRICS[key]}`
        );
        metric[metricName] = this.agentStatus[key];
      });
      return metric;
    },
    conversationMetrics() {
      let metric = {};
      Object.keys(this.accountConversationMetric).forEach(key => {
        const metricName = this.$t(
          `OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.${OVERVIEW_METRICS[key]}`
        );
        metric[metricName] = this.accountConversationMetric[key];
      });
      return metric;
    },
    agentOutgoingMessagesCollection() {
      return this.buildAgentDistributionCollection('outgoingMessagesCount');
    },
    agentActiveConversationsCollection() {
      return this.buildAgentDistributionCollection('activeConversationsCount');
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.initalizeReport();
  },
  beforeUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  },
  methods: {
    initalizeReport() {
      this.fetchAllData();
      this.scheduleReportRefresh();
    },
    scheduleReportRefresh() {
      this.timeoutId = setTimeout(async () => {
        await this.fetchAllData();
        this.scheduleReportRefresh();
      }, FETCH_INTERVAL);
    },
    fetchAllData() {
      this.fetchAccountConversationMetric();
      this.fetchAgentConversationMetric();
      this.fetchAgentSummaryReports();
      this.fetchHeatmapData();
    },
    getLast7DaysRange() {
      const until = endOfDay(new Date());
      const since = startOfDay(subDays(until, 6));

      return {
        since: getUnixTime(since),
        until: getUnixTime(until),
      };
    },
    fetchAgentSummaryReports() {
      const { since, until } = this.getLast7DaysRange();

      this.$store.dispatch('summaryReports/fetchAgentSummaryReports', {
        since,
        until,
        businessHours: false,
      });
    },
    downloadHeatmapData() {
      let to = endOfDay(new Date());

      this.$store.dispatch('downloadAccountConversationHeatmap', {
        to: getUnixTime(to),
      });
    },
    fetchHeatmapData() {
      if (this.uiFlags.isFetchingAccountConversationsHeatmap) {
        return;
      }

      // the data for the last 6 days won't ever change,
      // so there's no need to fetch it again
      // but we can write some logic to check if the data is already there
      // if it is there, we can refetch data only for today all over again
      // and reconcile it with the rest of the data
      // this will reduce the load on the server doing number crunching
      let to = endOfDay(new Date());
      let from = startOfDay(subDays(to, 6));

      if (this.accountConversationHeatmap.length) {
        to = endOfDay(new Date());
        from = startOfDay(to);
      }

      this.$store.dispatch('fetchAccountConversationHeatmap', {
        metric: 'conversations_count',
        from: getUnixTime(from),
        to: getUnixTime(to),
        groupBy: 'hour',
        businessHours: false,
      });
    },
    getAgentLabel(agentId) {
      const agent = this.agents.find(item => item.id === Number(agentId));
      return agent?.name || agent?.available_name || this.$t('SUMMARY_REPORTS.AGENT');
    },
    buildAgentDistributionCollection(metricKey) {
      const palette = [
        '#0F766E',
        '#2563EB',
        '#EA580C',
        '#7C3AED',
        '#D97706',
        '#059669',
        '#DC2626',
        '#0891B2',
        '#4F46E5',
        '#65A30D',
      ];

      const reportData = this.agentSummaryReports
        .map(report => ({
          label: this.getAgentLabel(report.id),
          value: report[metricKey] || 0,
        }))
        .filter(report => report.value > 0)
        .sort((left, right) => right.value - left.value);

      return {
        labels: reportData.map(report => report.label),
        datasets: [
          {
            data: reportData.map(report => report.value),
            backgroundColor: reportData.map((_, index) => palette[index % palette.length]),
            borderColor: reportData.map((_, index) => palette[index % palette.length]),
            borderWidth: 2,
          },
        ],
      };
    },
    fetchAccountConversationMetric() {
      this.$store.dispatch('fetchAccountConversationMetric', {
        type: 'account',
      });
    },
    fetchAgentConversationMetric() {
      this.$store.dispatch('fetchAgentConversationMetric', {
        type: 'agent',
        page: this.pageIndex + 1,
      });
    },
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      this.fetchAgentConversationMetric();
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('OVERVIEW_REPORTS.HEADER')" />
  <div class="flex flex-col gap-4 pb-6">
    <div class="flex flex-col items-center md:flex-row gap-4">
      <div
        class="flex-1 w-full max-w-full md:w-[65%] md:max-w-[65%] conversation-metric"
      >
        <MetricCard
          :header="$t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.HEADER')"
          :is-loading="uiFlags.isFetchingAccountConversationMetric"
          :loading-message="
            $t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.LOADING_MESSAGE')
          "
        >
          <div
            v-for="(metric, name, index) in conversationMetrics"
            :key="index"
            class="flex-1 min-w-0 pb-2"
          >
            <h3 class="text-base text-n-slate-11">
              {{ name }}
            </h3>
            <p class="text-n-slate-12 text-3xl mb-0 mt-1">
              {{ metric }}
            </p>
          </div>
        </MetricCard>
      </div>
      <div class="flex-1 w-full max-w-full md:w-[35%] md:max-w-[35%]">
        <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER')">
          <div
            v-for="(metric, name, index) in agentStatusMetrics"
            :key="index"
            class="flex-1 min-w-0 pb-2"
          >
            <h3 class="text-base text-n-slate-11">
              {{ name }}
            </h3>
            <p class="text-n-slate-12 text-3xl mb-0 mt-1">
              {{ metric }}
            </p>
          </div>
        </MetricCard>
      </div>
    </div>
    <div class="flex flex-row flex-wrap max-w-full">
      <MetricCard :header="$t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.HEADER')">
        <template #control>
          <woot-button
            icon="arrow-download"
            size="small"
            variant="smooth"
            color-scheme="secondary"
            @click="downloadHeatmapData"
          >
            {{ $t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.DOWNLOAD_REPORT') }}
          </woot-button>
        </template>
        <ReportHeatmap
          :heat-data="accountConversationHeatmap"
          :is-loading="uiFlags.isFetchingAccountConversationsHeatmap"
        />
      </MetricCard>
    </div>
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <AgentDistributionCard
        :header="$t('OVERVIEW_REPORTS.AGENT_OUTGOING_MESSAGES.HEADER')"
        :badge-label="$t('OVERVIEW_REPORTS.LAST_7_DAYS')"
        :collection="agentOutgoingMessagesCollection"
        :is-loading="summaryReportsUIFlags.isFetchingAgentSummaryReports"
        :loading-message="
          $t('OVERVIEW_REPORTS.AGENT_OUTGOING_MESSAGES.LOADING_MESSAGE')
        "
        :empty-message="$t('OVERVIEW_REPORTS.AGENT_OUTGOING_MESSAGES.NO_DATA')"
      />
      <AgentDistributionCard
        :header="$t('OVERVIEW_REPORTS.AGENT_ACTIVE_CONVERSATIONS.HEADER')"
        :badge-label="$t('OVERVIEW_REPORTS.LAST_7_DAYS')"
        :collection="agentActiveConversationsCollection"
        :is-loading="summaryReportsUIFlags.isFetchingAgentSummaryReports"
        :loading-message="
          $t('OVERVIEW_REPORTS.AGENT_ACTIVE_CONVERSATIONS.LOADING_MESSAGE')
        "
        :empty-message="$t('OVERVIEW_REPORTS.AGENT_ACTIVE_CONVERSATIONS.NO_DATA')"
      />
    </div>
    <div class="flex flex-row flex-wrap max-w-full">
      <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.HEADER')">
        <AgentTable
          :agents="agents"
          :agent-metrics="agentConversationMetric"
          :page-index="pageIndex"
          :is-loading="uiFlags.isFetchingAgentConversationMetric"
          @page-change="onPageNumberChange"
        />
      </MetricCard>
    </div>
  </div>
</template>
